/**
 * The score table: one row per metric (overall, each evaluator, cost per item,
 * latency p50), one column per experiment, and each value's change against the
 * reference — blue when better, orange when worse. Higher is better for
 * scores, lower for cost and latency.
 */

import { RemappedBadge } from "@/features/experiments/detail/remapped-badge";
import type { ExperimentEvaluator, ExperimentSummary } from "@/features/experiments/types";
import { COST, LATENCY, changeCounts, scoreMetrics, toneOf, type ItemFilter, type Metric } from "./metrics";
import { SeriesLabel, TONE_CLASS } from "./series-label";
import type { Comparison, ComparePoint } from "./types";

function Cell({
  metric,
  point,
  against,
  isRef,
  evaluator,
  counts,
  onOpen,
}: {
  evaluator?: ExperimentEvaluator | undefined;
  counts?: { better: number; worse: number } | undefined;
  onOpen?: ((dir: "better" | "worse") => void) | undefined;
  metric: Metric;
  point: ComparePoint | undefined;
  against: ComparePoint | undefined;
  isRef: boolean;
}) {
  const value = metric.get(point);
  const base = metric.get(against);
  const diff = value !== null && base !== null && !isRef && !metric.mismatch ? value - base : null;
  const version = point && metric.mismatch ? metric.versions?.[point.experiment_id] : undefined;
  return (
    <td className={`num ${isRef ? "bg-subtle" : ""}`}>
      <span className="inline-flex flex-col items-end gap-0.5">
        <span className="inline-flex items-baseline justify-end gap-1.5">
          <span>{metric.format(value)}</span>
          {version != null && <span className="font-mono text-[11px] text-warn">v{version}</span>}
          <RemappedBadge version={evaluator?.version} remapped={evaluator?.remapped} />
          {diff !== null && (
            <span className={`text-[11px] ${TONE_CLASS[toneOf(diff, metric.better)]}`}>
              {metric.formatDelta(diff, base as number)}
            </span>
          )}
        </span>
        {counts && onOpen && (
          <span className="text-[11px] text-muted">
            <button
              type="button"
              className="link"
              aria-label={`${counts.better} items better on ${metric.label}`}
              onClick={() => onOpen("better")}
            >
              ▲ {counts.better} better
            </button>
            {" · "}
            <button
              type="button"
              className="link"
              aria-label={`${counts.worse} items worse on ${metric.label}`}
              onClick={() => onOpen("worse")}
            >
              ▼ {counts.worse} worse
            </button>
          </span>
        )}
      </span>
    </td>
  );
}

export function CompareScores({
  data,
  refId,
  onOpenItems,
}: {
  data: Comparison;
  refId: string;
  /** Opens the Items tab on `filter`, narrowed to one evaluator and experiment. */
  onOpenItems?: (filter: ItemFilter, evaluator: string, vs: string) => void;
}) {
  const points = new Map(data.points.map((p) => [p.experiment_id, p]));
  const ref = points.get(refId);
  const metrics = [...scoreMetrics(data), COST, LATENCY];
  const canCount = (m: Metric) =>
    data.same_dataset_version && !m.mismatch && data.evaluators.some((ev) => ev.key === m.key);
  const costNotes = data.points.filter((p) => p.cost_note);
  return (
    <section className="box" aria-label="Scores">
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 w-[200px]">Metric</th>
              {data.experiments.map((e: ExperimentSummary, i) => (
                <th key={e.id} className="num">
                  <SeriesLabel experiment={e} index={i} isRef={e.id === refId} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.key}>
                <th scope="row" className="sticky left-0 z-10 bg-surface! text-left font-normal">
                  <span className="flex flex-col gap-px">
                    <span className={`text-[13px] text-ink ${m.key === "overall" ? "font-semibold" : ""}`}>
                      {m.label}
                    </span>
                    <span className="text-[11px] text-muted">{m.note}</span>
                  </span>
                </th>
                {data.experiments.map((e) => (
                  <Cell
                    key={e.id}
                    metric={m}
                    point={points.get(e.id)}
                    against={ref}
                    isRef={e.id === refId}
                    evaluator={e.evaluators.find((ev) => ev.key === m.key)}
                    {...(onOpenItems && canCount(m) && e.id !== refId
                      ? {
                          counts: changeCounts(data, refId, e.id, m.key),
                          onOpen: (dir: "better" | "worse") => onOpenItems(dir, m.key, e.id),
                        }
                      : {})}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {costNotes.length > 0 && (
        <p className="border-t border-line-soft bg-well px-4 py-2.5 text-xs text-muted">
          {costNotes.length === data.points.length
            ? "Agent cost is unknown for these experiments, so cost per item counts evaluator spend only."
            : `${costNotes.map((p) => data.experiments.find((e) => e.id === p.experiment_id)?.name).join(", ")}: ${costNotes[0]?.cost_note}`}
        </p>
      )}
    </section>
  );
}
